// The user's plan. Only the backend works it out and writes it (backend/plans.py):
// the app reads `plan` / `planSource` from the user doc and never sets them.

import { useEffect, useRef } from "react";
import { auth } from "../firebase";
import type { FirestoreUserData } from "../types/user";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

export function isPremium(user: FirestoreUserData | null | undefined): boolean {
  return user?.plan === "premium";
}

/** One line for the plan, by where it comes from. */
export function planLine(user: FirestoreUserData | null | undefined): string {
  if (!isPremium(user)) return "Basic plan · Limited features";
  if (user?.planSource === "stripe") return "$5/month · Premium features";
  if (user?.planSource === "comp") return "Comped · Premium features";
  return "Early access · Every feature, free while Caramel is in beta";
}

/** Ask the backend to work out and save the caller's plan (POST /me). */
export async function ensurePlan(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    const response = await fetch(`${API_BASE}/me`, {
      method: "POST",
      headers: { Authorization: `Bearer ${await user.getIdToken()}` },
    });
    if (!response.ok) console.error("Plan check failed:", response.status);
  } catch (e) {
    console.error("Plan check failed:", e);
  }
}

/** Users from before plans existed (or whose sign-in call failed) get one once their doc loads. */
export function useEnsurePlan(user: FirestoreUserData | null, loading: boolean) {
  const asked = useRef(false);
  useEffect(() => {
    if (loading || !user || user.plan || asked.current) return;
    asked.current = true;
    ensurePlan();
  }, [user, loading]);
}
